import React, { useState, useEffect, useRef, useMemo } from "react";
import dynamic from "next/dynamic";
import styled from "styled-components";
import { dateListOffer } from "@/lib/Types/DateListResponse/DateListResponse";
import {
  OfferStatusTypes,
  QuickVacancy,
  isAvailableStatus,
} from "@/lib/Types/DateListResponse/QuickVacancy";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { usePathname } from "next/navigation";
import { fetchQuickVacancy } from "@/app/api/quickVacancy";
import { fetchUpsellingOffers } from "@/app/api/fetchUpsellingOffers";
import { fetchAlternativeFlights } from "@/app/api/fetchAlternativeFlights";
import { AlternativeFlightOffer } from "@/lib/Types/AlternativeFlight";
import { fetchVacancy } from "@/app/api/vacancy";
import { isApiError } from "@/lib/Types/ApiError";
import { buildBookingUrl } from "../../Helpers/buildBookingUrl";
import useTracking from "@/lib/startrack/hooks/useTracking";
import { useIsLoggedIn } from "@/lib/Hooks/useIsLoggedIn";
import { getPaymentTypes } from "@/lib/Helpers/paymentTypes";
import OfferHeader from "./sections/OfferHeader";
import OfferDetails from "./sections/OfferDetails";
import OfferFlights from "./sections/OfferFlights";
import OfferPrice from "./sections/OfferPrice/OfferPrice";
import { channel } from "@/lib/Types/Channel";
import { shouldShowWegPerks } from "@/lib/Helpers/wegPerks";
import { AppConfig } from "@/lib/Types/AppConfig";
import { getMinBaggageAllowance } from "@/lib/Helpers/getMinBaggageAllowance";

const OfferDetailsDialog = dynamic(
  () => import("./dialogs/OfferDetailsDialog")
);
const FlightDetailsDialog = dynamic(
  () => import("./dialogs/FlightDetailsDialog")
);
const UpsellingOffersDialog = dynamic(
  () => import("./dialogs/UpsellingOffersDialog/UpsellingOffersDialog")
);
const AlternativeFlightsDialog = dynamic(
  () => import("./dialogs/AlternativeFlightsDialog")
);

import media from "@/styles/mediaQueries";
import { uidGenerator } from "@/lib/Helpers/uidGenerator";
import { StartrackingEventsNames } from "@/lib/startrack/trackingEvents";
import { compareFlights } from "./helpers/compareFlights";
import { useHotelInfoStore } from "@/lib/stores/hotelInfoStore";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { getFreshClickId } from "@/lib/Helpers/getFreshClickId";

const StyledCard = styled.div<{
  $isAvailable: boolean;
  $isUnavailable: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.kallisto.border.radius.radius100};
  overflow: hidden;
  border: ${(props) => (props.$isAvailable ? "2px solid" : "1px solid")};
  border-color: ${(props) =>
    props.$isAvailable
      ? props.theme.kallisto.color.border.interactive.borderNeutralPressed
      : props.theme.kallisto.color.border.static.borderNeutral};
  background-color: ${(props) =>
    props.theme.kallisto.color.background.static.backgroundPage};
  ${(props) =>
    props.$isUnavailable &&
    `
    opacity: 0.5;
    pointer-events: none;

  `}
`;

const Body = styled.div<{ $hasFlights: boolean }>(({ $hasFlights }) => ({
  padding: "1rem",
  display: "grid",
  gap: "1rem",
  alignItems: "stretch",
  gridTemplateAreas: $hasFlights
    ? `"details" "flights" "price"`
    : `"details" "price"`,
  gridTemplateColumns: "1fr",
  ...media.screenSmMin({
    gridTemplateAreas: $hasFlights
      ? `"details flights" "price price"`
      : `"details price"`,
    gridTemplateColumns: $hasFlights ? "1fr 1fr" : "1fr 1fr",
  }),
  ...media.screenMdMin({
    gridTemplateAreas: $hasFlights
      ? `"details flights price"`
      : `"details price"`,
    gridTemplateColumns: $hasFlights ? "1fr 1fr 1fr" : "2fr 1fr",
  }),
}));

enum CheckState {
  UNCHECKED = "UNCHECKED",
  CHECKING = "CHECKING",
  CHECKED = "CHECKED",
}

interface OfferListItemProps {
  offer: dateListOffer;
  searchId: string;
  idx: number;
  shouldPrefetch?: boolean;
  appConfig: AppConfig;
}

const OfferListItem: React.FC<OfferListItemProps> = ({
  offer,
  searchId,
  shouldPrefetch = false,
  idx,
  appConfig,
}) => {
  const searchParams = useReactiveSearchParams();
  const pathname = usePathname();
  const { userData, viewMode } = appConfig;
  const hotelCid = useHotelInfoStore((state) => state.hotelInfo.hotelCid);

  // Comvel Hotel ID for tracking productId (schema requires a non-null string).
  // Prefer the store value (populated from hotelData.id), but fall back to the
  // URL cid_ — the same canonical Comvel ID, available synchronously. The store
  // is set in an async effect (HotelTitleClient) and can still be null when an
  // early CHECK_AVAILABILITY / AUTO-prefetch fires, which was sending
  // productId: null and failing Avro conversion (MKTP-111 regression).
  const productHotelId = hotelCid ?? pathname?.match(/cid_(\d+)/)?.[1] ?? "";

  const logInteraction = (message: string, jsonMessage?: Record<string, unknown>) => {
    sendKibanaLog({ envConfig: appConfig.envConfig, message, category: "interaction", searchId, jsonMessage });
  };
  const preselectedOfferId = searchParams.get("preselectedOfferId");
  const isLoggedIn = useIsLoggedIn();
  const { track } = useTracking(appConfig);
  const userId = userData?.userUid;

  // Upselling state - displayedOffer holds either original or selected upselling offer
  const [displayedOffer, setDisplayedOffer] = useState<dateListOffer>(offer);
  const [upsellingOffers, setUpsellingOffers] = useState<dateListOffer[]>([]);
  const [isFetchingUpsellingOffers, setIsFetchingUpsellingOffers] =
    useState(false);
  const [hasNoUpsellingOffers, setHasNoUpsellingOffers] = useState(false);
  const [isUpsellingDialogOpen, setIsUpsellingDialogOpen] = useState(false);
  const [selectedUpsellingOffer, setSelectedUpsellingOffer] =
    useState<dateListOffer | null>(null);

  // Alternative flights state
  const [alternativeFlights, setAlternativeFlights] = useState<
    AlternativeFlightOffer[]
  >([]);
  const [isFetchingAlternativeFlights, setIsFetchingAlternativeFlights] =
    useState(false);
  const [hasNoAlternativeFlights, setHasNoAlternativeFlights] = useState(false);
  const [isAlternativeFlightsDialogOpen, setIsAlternativeFlightsDialogOpen] =
    useState(false);

  // Dialog states
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isFlightDetailsDialogOpen, setIsFlightDetailsDialogOpen] =
    useState(false);

  // Vacancy state
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  // Use displayedOffer for rendering (either original or selected upselling offer)
  const {
    price,
    estimatedTotalPrice,
    quickVacancy,
    bookingInfo,
    departureAirport,
    destinationAirport,
    peakworkResponse,
    tourOperator,
    from,
    to,
    duration,
    stayDuration,
    flights,
    offerRoomDetails,
    mealType,
    transfer,
    rentalCar,
    railAndFly,
    flexibleOfferData,
    freeRebookingOfferData,
    specialDealsDetails,
    giataObjectCode,
    objectCode,
    ttMealType,
    roomType,
    hasFlexibleOffer,
    estimatedTotalPriceRrp,
    accomodationCode,
    upsellingOfferInfos,
    lastminuteHotelDetailUrl,
  } = displayedOffer;

  const [quickVacancyContent, setQuickVacancyContent] =
    useState<QuickVacancy | null>(quickVacancy);

  // Find book on option from special deals — prefer QV response (verified by backend) over datelist
  const bookOnOptionDeal =
    quickVacancyContent?.specialDealsDetails?.find(
      (deal) => deal.specialDealTypes === "BOOK_ON_OPTION"
    ) ??
    specialDealsDetails?.find(
      (deal) => deal.specialDealTypes === "BOOK_ON_OPTION"
    );

  // Store the original offer's QuickVacancy result so we can restore it
  const originalQuickVacancyRef = useRef<QuickVacancy | null>(null);

  // If quickVacancy is already present (cached from backend), mark as checked
  const [checkState, setCheckState] = useState<CheckState>(
    quickVacancy ? CheckState.CHECKED : CheckState.UNCHECKED
  );

  // Derived availability states (matching old app pattern)
  const isChecked = checkState === CheckState.CHECKED;
  const isAvailable = isAvailableStatus(quickVacancyContent?.status?.type);
  const isUnavailable = isChecked && !isAvailable;

  // Detect flight changes after QuickVacancy check
  const flightChanges = useMemo(() => {
    if (!isChecked || !quickVacancyContent?.flights) {
      return { outwardFlightChanged: false, returnFlightChanged: false };
    }
    return compareFlights(flights, quickVacancyContent.flights);
  }, [isChecked, flights, quickVacancyContent?.flights]);

  // Check if TO_VOUCHER deal was invalidated after QuickVacancy check
  const getToVoucherInvalidated = () => {
    if (!quickVacancyContent) return false;
    const originalToVoucher = specialDealsDetails?.find(
      (deal) => deal.specialDealTypes === "TO_VOUCHER"
    );
    const qvToVoucher = quickVacancyContent.specialDealsDetails?.find(
      (deal) => deal.specialDealTypes === "TO_VOUCHER"
    );
    return !!(originalToVoucher?.valid && qvToVoucher && !qvToVoucher.valid);
  };
  const toVoucherInvalidated = getToVoucherInvalidated();

  // Call startrack after availability check
  const trackAvalability = (result: QuickVacancy | null) => {
    track(StartrackingEventsNames.CheckAvailability, {
      searchId: searchId,
      clickId: getFreshClickId(),
      productId: productHotelId,
      userUid: userId,
      isLoggedIn: isLoggedIn,
      to: result?.tourOperator?.code ?? "",
      checkAvailabilityType: result?.upsellingOffer ? "UPGRADE" : "BASIC", // TODO this is always false
      provider: result?.bookingInfo?.offerID?.includes("PEAKWORK")
        ? "Peakwork"
        : "Amadeus",
      response: result?.status?.type ?? "",
      CTA: "LIST", // This value is meant for TOP offers, a deprecated feature
      originalPrice: offer?.estimatedTotalPrice ?? 0,
      quotedPrice: result?.travelPriceSum ?? 0,
      position: idx + 1,
    });
  };

  // Handle quickVacancy fetching
  const handleCheckAvailability = async (
    type: "MANUAL" | "AUTO" = "MANUAL"
  ) => {
    // Update preselectedOfferId param in URL only on manual user interaction,
    // not during auto-prefetch (multiple concurrent prefetches would race and overwrite each other)
    if (type === "MANUAL" && bookingInfo?.offerID) {
      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set("preselectedOfferId", bookingInfo.offerID);
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${currentSearchParams.toString()}`
      );
    }

    setCheckState(CheckState.CHECKING);
    try {
      const result = await fetchQuickVacancy({
        searchParams: Object.fromEntries(searchParams.entries()),
        bookingInfo,
        peakworkResponse,
        departureAirport: departureAirport?.iataCode ?? "",
        destinationAirport: destinationAirport?.iataCode ?? "",
        tourOperatorCode: tourOperator.code,
        price,
        hotelCid: hotelCid ? parseInt(hotelCid) : null,
        estimatedTotalPrice: estimatedTotalPrice ?? 0,
        type,
        specialDealsDetails,
        appConfig,
      });
      // Fallback to original datelist flights when QV returns empty flights (matches Sunrise)
      if (
        result &&
        (!result.flights?.outwardFlight?.length ||
          !result.flights?.returnFlight?.length) &&
        flights
      ) {
        result.flights = flights;
      }
      setQuickVacancyContent(result);
      // Store the original offer's QV so we can restore it later
      originalQuickVacancyRef.current = result;
      trackAvalability(result);
      setCheckState(CheckState.CHECKED);
    } catch (error) {
      console.error("Error fetching quick vacancy:", error);
    }
  };

  // Auto-trigger QuickVacancy on page load if this offer is preselected or should prefetch
  useEffect(() => {
    // External-redirect offers (e.g. lastminute) bypass the booking flow entirely
    if (lastminuteHotelDetailUrl) return;

    const shouldAutoCheck =
      (preselectedOfferId && preselectedOfferId === bookingInfo?.offerID) ||
      shouldPrefetch;

    if (shouldAutoCheck && checkState === CheckState.UNCHECKED) {
      handleCheckAvailability("AUTO");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedOfferId, bookingInfo?.offerID, checkState, shouldPrefetch]);

  // Handle upselling offers fetch
  const handleUpsellingClick = async () => {
    // If we already have offers cached, just open the dialog
    if (upsellingOffers.length > 0) {
      setSelectedUpsellingOffer(displayedOffer); // Use currently displayed offer
      setIsUpsellingDialogOpen(true);
      logInteraction("Upselling Dialog Opened");
      return;
    }

    setIsFetchingUpsellingOffers(true);
    try {
      const offers = await fetchUpsellingOffers({
        cid: hotelCid ?? "",
        from: searchParams.get("from") ?? "",
        to: searchParams.get("to") ?? "",
        mainOfferFromDate: from,
        mainOfferToDate: to,
        rooms: searchParams.get("rooms") ?? "",
        tourOperatorCode: tourOperator.code,
        mealType: mealType?.name ?? "",
        roomType: offerRoomDetails?.[0]?.name ?? "",
        roomName: offerRoomDetails?.[0]?.nameOffer ?? "",
        transferIncluded: transfer,
        price: price,
        flights: flights ?? {},
        searchId: searchId,
        transactionId: searchParams.get("transactionId") ?? undefined,
        appConfig,
        rentalCar: searchParams.get("rentalCar") === "1",
        transfer: searchParams.get("transfer") === "1",
        railAndFly: searchParams.get("railAndFly") === "1",
        directFlight: searchParams.get("directFlight") === "1",
        departureAirport: searchParams.get("departureAirport") ?? undefined,
        sort: searchParams.get("sort") ?? undefined,
        duration: searchParams.get("duration") ?? undefined,
      });

      setUpsellingOffers(offers);

      // If no upselling offers found, mark it and don't open dialog
      if (offers.length === 0) {
        setHasNoUpsellingOffers(true);
        return;
      }

      setSelectedUpsellingOffer(displayedOffer); // Use currently displayed offer
      setIsUpsellingDialogOpen(true);
      logInteraction("Upselling Dialog Opened");
      track(StartrackingEventsNames.CheckUpsellingOptions, {
        searchId: searchId,
        clickId: getFreshClickId(),
        productId: productHotelId,
        userUid: userId,
        isLoggedIn: isLoggedIn,
        to: offer?.tourOperator?.code ?? "",
        provider: offer?.bookingInfo?.offerID?.includes("PEAKWORK")
          ? "Peakwork"
          : "Amadeus",
        numberofUpgrades: offers?.length ?? 0,
      });

      // Check QuickVacancy for all upselling offers
      offers.forEach(async (upsellingOffer) => {
        if (
          upsellingOffer.bookingInfo?.offerID !== offer.bookingInfo?.offerID
        ) {
          try {
            const qvResult = await fetchQuickVacancy({
              searchParams: Object.fromEntries(searchParams.entries()),
              bookingInfo: upsellingOffer.bookingInfo,
              peakworkResponse: upsellingOffer.peakworkResponse,
              departureAirport: upsellingOffer.departureAirport?.iataCode ?? "",
              destinationAirport: upsellingOffer.destinationAirport?.iataCode ?? "",
              tourOperatorCode: upsellingOffer.tourOperator.code,
              price: upsellingOffer.price,
              hotelCid: hotelCid ? parseInt(hotelCid) : null,
              estimatedTotalPrice: upsellingOffer.estimatedTotalPrice ?? 0,
              type: "MANUAL",
              specialDealsDetails: upsellingOffer.specialDealsDetails,
              upsellingOffer: true,
              appConfig,
            });
            // Update the offer with quickVacancy result
            setUpsellingOffers((prev) =>
              prev.map((o) =>
                o.bookingInfo?.offerID === upsellingOffer.bookingInfo?.offerID
                  ? { ...o, quickVacancy: qvResult }
                  : o
              )
            );
            trackAvalability(qvResult);
          } catch (error) {
            console.error("Error checking upselling offer vacancy:", error);
          }
        }
      });
    } catch (error) {
      console.error("Error fetching upselling offers:", error);
    } finally {
      setIsFetchingUpsellingOffers(false);
    }
  };

  // Handle applying selected upselling offer
  const handleApplyUpselling = () => {
    if (selectedUpsellingOffer) {
      logInteraction("Upselling Offer Selected", { offerId: selectedUpsellingOffer.offerId });
      setDisplayedOffer(selectedUpsellingOffer);
      // Check if selecting original offer or an upselling offer
      const isSelectingOriginal =
        selectedUpsellingOffer.bookingInfo?.offerID ===
        offer.bookingInfo?.offerID;

      if (isSelectingOriginal && originalQuickVacancyRef.current) {
        // Restore the original offer's QV
        setQuickVacancyContent(originalQuickVacancyRef.current);
      } else if (selectedUpsellingOffer.quickVacancy) {
        // Use the upselling offer's QV
        setQuickVacancyContent(selectedUpsellingOffer.quickVacancy);
      }
      setIsUpsellingDialogOpen(false);
    }
  };

  // Handle alternative flights fetch
  const handleAlternativeFlightsClick = async () => {
    // If we already have flights cached, just open the dialog
    if (alternativeFlights.length > 0) {
      setIsAlternativeFlightsDialogOpen(true);
      logInteraction("Alternative Flights Dialog Opened");
      return;
    }

    setIsFetchingAlternativeFlights(true);
    try {
      const flights = await fetchAlternativeFlights({
        bookingCode:
          peakworkResponse?.offer?.accommodation?.rooms?.[0]?.bookingCode ?? "",
        offerId: bookingInfo?.offerID ?? "",
        transactionId: quickVacancyContent?.transactionId,
        departureAirport: departureAirport?.iataCode ?? "",
        destinationAirport: destinationAirport?.iataCode ?? "",
        giataId: giataObjectCode,
        objectCode: objectCode,
        accomodationCode: accomodationCode ?? objectCode,
        duration: duration,
        from: from,
        to: to,
        roomCode: accomodationCode ?? objectCode,
        roomType: roomType?.name ?? "",
        mealType: mealType?.name ?? "",
        ttMealType: ttMealType,
        tourOperatorCode: tourOperator.code,
        appConfig,
        ...(roomType?.addition && {
          roomAddition: roomType.addition,
        }),
      });

      // If no alternative flights found, mark it and don't open dialog
      if (flights.length === 0 || flights.length === 1) {
        setHasNoAlternativeFlights(true);
        return;
      }

      // Pre-populate first flight with existing QV data (it's the current offer)
      const flightsWithFirstQV = flights.map((f, index) =>
        index === 0 && quickVacancyContent
          ? { ...f, quickVacancy: quickVacancyContent }
          : f
      );
      setAlternativeFlights(flightsWithFirstQV);

      setIsAlternativeFlightsDialogOpen(true);
      logInteraction("Alternative Flights Dialog Opened");

      // Auto-check QuickVacancy for alternative flights (skip first, already has QV)
      flightsWithFirstQV.slice(1, 5).forEach(async (altFlight) => {
        try {
          const qvResult = await fetchQuickVacancy({
            searchParams: Object.fromEntries(searchParams.entries()),
            bookingInfo: altFlight.bookingInfo,
            peakworkResponse: altFlight?.peakworkResponse,
            departureAirport: departureAirport?.iataCode ?? "",
            destinationAirport: destinationAirport?.iataCode ?? "",
            tourOperatorCode: tourOperator.code,
            price: altFlight.price,
            hotelCid: hotelCid ? parseInt(hotelCid) : null,
            estimatedTotalPrice: altFlight.price,
            type: "AUTO",
            specialDealsDetails,
            appConfig,
          });
          // Update the flight with quickVacancy result
          setAlternativeFlights((prev) =>
            prev.map((f) =>
              f.bookingInfo?.offerID === altFlight.bookingInfo?.offerID
                ? { ...f, quickVacancy: qvResult }
                : f
            )
          );
        } catch (error) {
          console.error("Error checking alternative flight vacancy:", error);
        }
      });
    } catch (error) {
      console.error("Error fetching alternative flights:", error);
    } finally {
      setIsFetchingAlternativeFlights(false);
    }
  };

  // Handle booking an alternative flight (redirect to checkout)
  const handleBookAlternativeFlight = (altFlight: AlternativeFlightOffer) => {
    if (!altFlight.quickVacancy) return;

    handleBookOffer({
      quickVacancy: altFlight.quickVacancy,
      bookingInfoFallback: altFlight.bookingInfo,
      source: "alternative",
      isUpsellingOffer: false,
    });
  };

  // Unified booking handler for both main offer and alternative flights
  const handleBookOffer = async ({
    quickVacancy,
    bookingInfoFallback,
    source,
    isUpsellingOffer = false,
  }: {
    quickVacancy: QuickVacancy;
    bookingInfoFallback: typeof bookingInfo;
    source: "main" | "alternative";
    isUpsellingOffer?: boolean;
  }) => {
    if (!isAvailableStatus(quickVacancy.status?.type)) {
      return;
    }

    setIsBookingLoading(true);
    try {
      const bookingInfoToUse = quickVacancy.bookingInfo ?? bookingInfoFallback;

      // Prefer the QuickVacancy-verified TO_VOUCHER deal (ruleId/validity
      // confirmed by the backend) over the original datelist value, which can
      // be stale/invalidated after the "Angebot prüfen" check — same fallback
      // pattern already used for bookOnOptionDeal above.
      const toVoucherDealForBooking =
        quickVacancy.specialDealsDetails?.find(
          (deal) => deal.specialDealTypes === "TO_VOUCHER"
        ) ??
        specialDealsDetails?.find(
          (deal) => deal.specialDealTypes === "TO_VOUCHER"
        );
      const specialDealsDetailsForBooking = [
        ...(specialDealsDetails?.filter(
          (deal) => deal.specialDealTypes !== "TO_VOUCHER"
        ) ?? []),
        ...(toVoucherDealForBooking ? [toVoucherDealForBooking] : []),
      ];

      const result = await fetchVacancy({
        bookingInfo: bookingInfoToUse,
        departureAirportCode: departureAirport?.iataCode ?? "",
        destinationAirportCode: destinationAirport?.iataCode ?? "",
        departureDate: from,
        returnDate: to,
        tourOperatorCode: tourOperator.code,
        hasFlexibleOffer: hasFlexibleOffer ?? false,
        ruleId: flexibleOfferData?.ruleId ?? null,
        specialDealsDetails: specialDealsDetailsForBooking,
        transactionId: quickVacancy.transactionId,
        hotelCid: hotelCid ? parseInt(hotelCid) : 0,
        roomsString: searchParams.get("rooms") ?? "",
        skyscannerRedirectId: searchParams.get("skyscanner_redirectid"),
        upsellingOffer: isUpsellingOffer,
        appConfig,
        searchId
      });

      if (isApiError(result)) {
        console.error("[Vacancy] API Error:", result.message);
        if (source === "main") {
          setQuickVacancyContent(null);
        }
        setIsBookingLoading(false);
        return;
      }

      const selectionid = uidGenerator().generate("");

      track(StartrackingEventsNames.ProductDetailClicked, {
        searchId: searchId,
        userUid: userId,
        isLoggedIn: isLoggedIn,
        totalPrice:
          offer?.totalPrice ??
          offer?.estimatedTotalPrice ??
          quickVacancy.totalPrice ??
          0,
        productId: offer?.objectCode ?? "",
        clickoutid: searchParams.get("trackingId") ?? null,
        clickid: getFreshClickId(),
        provider: offer?.offerId?.includes("PEAKWORK") ? "Peakwork" : "Amadeus",
        to: offer?.tourOperator.code,
        selectionid: selectionid,
        upgradedOptionSelected: quickVacancy.isUpgrading ?? false,
        totalPriceBasicOption: quickVacancy.totalPriceBasicOption ?? 0,
        CTA: "LIST",
        position: idx + 1,
      });



      const bookingUrl = buildBookingUrl({
        vacancyResponse: result,
        hotelCid: hotelCid ?? "",
        objectCode: objectCode,
        hasFlexibleOffer: hasFlexibleOffer ?? false,
        flexibleOfferData: flexibleOfferData,
        specialDealsDetails: specialDealsDetailsForBooking,
        estimatedTotalPriceRrp: estimatedTotalPriceRrp,
        isUpsellingOffer: isUpsellingOffer,
        searchParams: new URLSearchParams(searchParams.toString()),
        selectionId: selectionid,
        clickId: getFreshClickId(),
        viewMode,
      });

      window.location.assign(bookingUrl);
    } catch (error) {
      console.error("[Vacancy] Error:", error);
      if (source === "main") {
        setQuickVacancyContent(null);
      }
      setIsBookingLoading(false);
    }
  };

  const onSubmitOffer = (status: OfferStatusTypes | undefined) => {
    if (!isAvailableStatus(status) || !quickVacancyContent) return;

    handleBookOffer({
      quickVacancy: quickVacancyContent,
      bookingInfoFallback: bookingInfo,
      source: "main",
      isUpsellingOffer: upsellingOfferInfos?.length > 0,
    });
  };

  // Get the original price for price difference calculation (use confirmed QV price if available)
  const originalPrice =
    originalQuickVacancyRef.current?.travelPriceSum ??
    offer.totalPrice ??
    offer.estimatedTotalPrice ??
    0;

  return (
    <StyledCard
      $isAvailable={isAvailable}
      $isUnavailable={isUnavailable}
      data-testid="offer-list-item"
    >
      <OfferHeader
        from={from}
        to={to}
        duration={duration}
        stayDuration={stayDuration}
        departureAirportCode={departureAirport?.iataCode}
        tourOperator={tourOperator}
        isAvailable={isAvailable}
        bookingInfo={bookingInfo}
        isDebugMode={searchParams.get("debugMode") === 'true'}
        source={displayedOffer.source}
      />
      <Body $hasFlights={viewMode.channel === channel.PACKAGE}>
        <OfferDetails
          offerRoomDetails={offerRoomDetails}
          mealType={mealType}
          transfer={transfer}
          rentalCar={rentalCar}
          railAndFly={railAndFly}
          flexibleOfferData={flexibleOfferData}
          freeRebookingOfferData={freeRebookingOfferData}
          bookOnOptionLabel={bookOnOptionDeal?.label}
          bookOnOptionTooltip={bookOnOptionDeal?.tooltip}
          oceanView={roomType?.addition}
          minBaggageAllowance={getMinBaggageAllowance(quickVacancyContent?.flights)}
          offerForLimitedMobility={quickVacancyContent?.offerForLimitedMobility}
          showDetailsLink={isChecked && isAvailable}
          onDetailsClick={() => {
            setIsDetailsDialogOpen(true);
            logInteraction("Offer Details Dialog Opened");
          }}
          onUpsellingClick={handleUpsellingClick}
          isFetchingUpsellingOffers={isFetchingUpsellingOffers}
          hasNoUpsellingOffers={hasNoUpsellingOffers}
          compactTooltips={viewMode.channel === channel.HOTEL}
          showWegPerks={shouldShowWegPerks(
            appConfig.WlConfig,
            viewMode.channel,
            appConfig.envConfig.currentEnv,
          )}
          WlConfig={appConfig.WlConfig}
        />
        {viewMode.channel === channel.PACKAGE && flights && (
          <OfferFlights
            flights={quickVacancyContent?.flights ?? flights}
            showDetailsLink={isChecked && isAvailable}
            onDetailsClick={() => {
              setIsFlightDetailsDialogOpen(true);
              logInteraction("Flight Details Dialog Opened");
            }}
            onAlternativeFlightsClick={handleAlternativeFlightsClick}
            isFetchingAlternativeFlights={isFetchingAlternativeFlights}
            hasNoAlternativeFlights={hasNoAlternativeFlights}
            isAlternativeFlightsDisabled={
              displayedOffer.offerId !== offer.offerId
            }
            outwardFlightChanged={flightChanges.outwardFlightChanged}
            returnFlightChanged={flightChanges.returnFlightChanged}
          />
        )}
        <OfferPrice
          price={price}
          estimatedTotalPrice={estimatedTotalPrice}
          confirmedTotalPrice={quickVacancyContent?.travelPriceSum ?? null}
          specialDealsDetails={specialDealsDetails}
          travellers={
            offerRoomDetails?.reduce(
              (acc, room) => acc + (room.numberOfPersons || 0),
              0
            ) || 1
          }
          isChecked={isChecked}
          isAvailable={isAvailable}
          isChecking={checkState === CheckState.CHECKING}
          onCheckAvailability={() => handleCheckAvailability()}
          onBookNow={() => onSubmitOffer(quickVacancyContent?.status?.type)}
          paymentTypes={getPaymentTypes(
            quickVacancyContent?.availablePaymentTypes
          )}
          tourOperator={quickVacancyContent?.tourOperator}
          offerRoomDetails={quickVacancyContent?.offerRoomDetails}
          showPriceList={isChecked && isAvailable}
          statusType={quickVacancyContent?.status?.type}
          toVoucherInvalidated={toVoucherInvalidated}
          isBookingLoading={isBookingLoading}
          lastminuteHotelDetailUrl={lastminuteHotelDetailUrl}
        />
      </Body>

      {isChecked && isAvailable && quickVacancyContent && (
        <OfferDetailsDialog
          isOpen={isDetailsDialogOpen}
          onClose={() => {
            setIsDetailsDialogOpen(false);
            logInteraction("Offer Details Dialog Closed");
          }}
          tourOperator={quickVacancyContent.tourOperator}
          offerForLimitedMobility={quickVacancyContent.offerForLimitedMobility}
          tourOperatorMessage={quickVacancyContent.tourOperatorInfo}
          from={from}
          to={to}
          gid={giataObjectCode}
          objectCode={objectCode}
          tourOperatorCode={tourOperator.code}
          transactionId={searchParams.get("transactionId") ?? undefined}
          appConfig={appConfig}
        />
      )}

      {isChecked &&
        isAvailable &&
        quickVacancyContent?.flights &&
        viewMode.channel === channel.PACKAGE && (
          <FlightDetailsDialog
            isOpen={isFlightDetailsDialogOpen}
            onClose={() => {
              setIsFlightDetailsDialogOpen(false);
              logInteraction("Flight Details Dialog Closed");
            }}
            flights={quickVacancyContent.flights}
            stayDuration={stayDuration}
          />
        )}

      {isChecked && isAvailable && (
        <UpsellingOffersDialog
          isOpen={isUpsellingDialogOpen}
          onClose={() => {
            setIsUpsellingDialogOpen(false);
            setSelectedUpsellingOffer(displayedOffer);
            logInteraction("Upselling Dialog Closed");
          }}
          upsellingOffers={upsellingOffers}
          originalOffer={offer}
          originalPrice={originalPrice}
          selectedOffer={selectedUpsellingOffer}
          onSelectOffer={setSelectedUpsellingOffer}
          onApply={handleApplyUpselling}
          isLoading={isFetchingUpsellingOffers}
        />
      )}

      {isChecked && isAvailable && (
        <AlternativeFlightsDialog
          isOpen={isAlternativeFlightsDialogOpen}
          onClose={() => {
            setIsAlternativeFlightsDialogOpen(false);
            logInteraction("Alternative Flights Dialog Closed");
          }}
          alternativeFlights={alternativeFlights}
          isLoading={isFetchingAlternativeFlights}
          originalPrice={originalPrice}
          onBookFlight={handleBookAlternativeFlight}
          stayDuration={stayDuration}
        />
      )}
    </StyledCard>
  );
};

export default OfferListItem;
